import { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check, Loader2, Mail } from "lucide-react";
import { PRODUCTS } from "@/data/catalogue";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BUYER_TYPES = ["Food Manufacturer", "Cosmetics", "Nutraceutical", "Pharma / Diagnostics", "Distributor", "Research / Institutional", "Other"];
const REQUEST_TYPES = [
  { id: "reserve", label: "Reserve Allocation", desc: "Reserve a portion of the July 2027 production allocation for your team." },
  { id: "technical", label: "Technical Review", desc: "Receive a technical dossier and discuss purity, format, and fit for your application." },
  { id: "documentation", label: "Documentation Review", desc: "Review the documentation framework for your regulated-market entry plan." },
  { id: "distributor", label: "Distributor Discussion", desc: "Explore regional partnership for the July 2027 rollout (also see the Distributor Portal)." },
  { id: "e40", label: "E40 Application Discussion", desc: "Discuss advanced technical-grade fit for diagnostics, fluorescence-linked, or institutional workflows." },
];

const STEPS = ["Company", "Application", "Request"];

const ReserveAllocation = () => {
  const [params] = useSearchParams();
  const preselectedProduct = params.get("product");
  const preselectedIntent = params.get("intent");

  const initialRequestType = REQUEST_TYPES.find((r) => r.id === preselectedIntent)?.id || "reserve";

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    country: "",
    buyer_type: "Food Manufacturer",
    products: preselectedProduct ? [preselectedProduct] : [],
    intended_application: "",
    preferred_format: "",
    quantity_per_month: "",
    target_timeline: "",
    destination_market: "",
    documentation_needed: "",
    certifications_required: "",
    request_type: initialRequestType,
    message: "",
  });

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleProduct = (id) =>
    setForm((f) => ({ ...f, products: f.products.includes(id) ? f.products.filter((p) => p !== id) : [...f.products, id] }));

  const canProceed = useMemo(() => {
    if (step === 0) return form.company_name && form.contact_name && form.email && form.country;
    if (step === 1) return form.products.length > 0;
    return form.request_type;
  }, [step, form]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiry`, form);
      setSuccess(true);
      toast.success("Your request has been logged for SPIRUVA's pre-launch review.");
    } catch (e) {
      console.error(e);
      toast.error("Submission failed. Please try again or email export@spiruva.com");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div data-testid="page-reserve-allocation" className="pt-[72px]">
        <section className="container-narrow py-20 md:py-32 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-brand-green text-brand-cream mb-8">
            <Check size={28} />
          </div>
          <p className="eyebrow mb-4">◦  Inquiry Received</p>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl text-brand-black leading-tight tracking-tight">
            Thank you. <span className="italic text-brand-teal">Logged for review.</span>
          </h1>
          <p className="mt-6 text-brand-black/70 max-w-xl mx-auto leading-relaxed text-sm md:text-base">
            Your request has been logged for SPIRUVA's pre-launch review process. We will assess application fit, documentation needs, and allocation relevance for the July 2027 commercial timeline, and respond to <span className="font-mono text-brand-teal">{form.email}</span>.
          </p>
          <a href="mailto:export@spiruva.com" className="btn-secondary mt-10 inline-flex"><Mail size={14} /> export@spiruva.com</a>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="page-reserve-allocation" className="pt-[72px]">
      <section className="container-wide py-12 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
        {/* LEFT: copy */}
        <div className="lg:col-span-4">
          <p className="eyebrow mb-4 md:mb-5">◦  Reserve Allocation</p>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl text-brand-black tracking-tight leading-[1.1]">
            Start your 2027 supply <span className="italic text-brand-teal">conversation.</span>
          </h1>
          <p className="text-brand-black/70 mt-5 md:mt-6 leading-relaxed text-sm md:text-base">
            This inquiry is for technical review, application-fit evaluation, and reserve-allocation planning ahead of July 2027 commercial readiness. It is not a direct purchase or instant quote form.
          </p>
          <ul className="mt-8 md:mt-10 space-y-3 md:space-y-4 text-sm text-brand-black/75">
            {[
              "Application-fit assessment by our technical desk",
              "Documentation pathway and traceability framework",
              "Allocation prioritisation for July 2027 production",
              "Confidential exchange — NDA available on request",
            ].map((x) => (
              <li key={x} className="flex gap-3"><Check size={16} className="text-brand-gold mt-0.5 shrink-0" />{x}</li>
            ))}
          </ul>
        </div>

        {/* RIGHT: form */}
        <div className="lg:col-span-8 bg-white border border-brand-green/15 p-6 sm:p-8 md:p-12">
          {/* progress */}
          <div className="flex items-center gap-2 sm:gap-3 mb-8 md:mb-10">
            {STEPS.map((s, i) => (
              <div key={s} className="flex items-center gap-2 sm:gap-3 flex-1">
                <div className={`flex items-center gap-2 sm:gap-3 ${i === step ? "text-brand-green" : i < step ? "text-brand-teal" : "text-brand-black/30"}`}>
                  <span className={`w-7 h-7 flex items-center justify-center font-mono text-xs border ${i === step ? "bg-brand-green text-brand-cream border-brand-green" : i < step ? "bg-brand-teal text-brand-cream border-brand-teal" : "border-brand-black/20"}`}>
                    {i < step ? <Check size={12} /> : i + 1}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-widest hidden sm:inline">{s}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`flex-1 h-px ${i < step ? "bg-brand-teal" : "bg-brand-black/15"}`} />}
              </div>
            ))}
          </div>

          {step === 0 && (
            <div data-testid="step-company" className="space-y-5">
              <h2 className="font-display text-2xl md:text-3xl text-brand-black">Company Profile</h2>
              <FormField label="Company *" value={form.company_name} onChange={(v) => setField("company_name", v)} testid="input-company-name" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <FormField label="Name *" value={form.contact_name} onChange={(v) => setField("contact_name", v)} testid="input-contact-name" />
                <FormField label="Email *" type="email" value={form.email} onChange={(v) => setField("email", v)} testid="input-email" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <FormField label="Phone / WhatsApp" value={form.phone} onChange={(v) => setField("phone", v)} testid="input-phone" />
                <FormField label="Country *" value={form.country} onChange={(v) => setField("country", v)} testid="input-country" />
              </div>
              <div>
                <Label>Buyer Type</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {BUYER_TYPES.map((t) => (
                    <button
                      key={t}
                      type="button"
                      data-testid={`buyer-type-${t.replace(/[\s/]+/g, '-')}`}
                      onClick={() => setField("buyer_type", t)}
                      className={`px-4 py-2 text-xs font-mono uppercase tracking-widest border transition min-h-[44px] ${form.buyer_type === t ? "bg-brand-green text-brand-cream border-brand-green" : "border-brand-green/25 text-brand-black/70 hover:border-brand-gold"}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div data-testid="step-products" className="space-y-5">
              <h2 className="font-display text-2xl md:text-3xl text-brand-black">Application & Volume</h2>
              <p className="text-sm text-brand-black/65">Select grade(s) of interest and tell us about your application context.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {PRODUCTS.map((p) => {
                  const selected = form.products.includes(p.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      data-testid={`product-select-${p.id}`}
                      onClick={() => toggleProduct(p.id)}
                      className={`text-left p-4 sm:p-5 border transition min-h-[88px] ${selected ? "border-brand-green bg-brand-green/5" : "border-brand-green/20 hover:border-brand-gold"}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <span className="font-mono text-[10px] uppercase tracking-widest text-brand-black/45">Grade · {p.grade}</span>
                        <span className={`w-5 h-5 border flex items-center justify-center shrink-0 ${selected ? "bg-brand-green border-brand-green text-brand-cream" : "border-brand-black/30"}`}>
                          {selected && <Check size={12} />}
                        </span>
                      </div>
                      <p className="font-display text-lg sm:text-xl mt-3 text-brand-black leading-tight">{p.name}</p>
                      <p className="text-xs text-brand-black/60 mt-1">{p.tier}</p>
                    </button>
                  );
                })}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-3">
                <FormField label="Intended Application" placeholder="e.g. blue beverage line, cosmetic serum, diagnostic kit" value={form.intended_application} onChange={(v) => setField("intended_application", v)} testid="input-application" />
                <FormField label="Preferred Format" placeholder="Powder · Liquid · Custom" value={form.preferred_format} onChange={(v) => setField("preferred_format", v)} testid="input-format" />
                <FormField label="Expected Annual Volume" placeholder="e.g. 50 kg / 1 MT" value={form.quantity_per_month} onChange={(v) => setField("quantity_per_month", v)} testid="input-quantity" />
                <FormField label="Target Approval / Launch Timeline" placeholder="e.g. Q3 2027" value={form.target_timeline} onChange={(v) => setField("target_timeline", v)} testid="input-timeline" />
                <FormField label="Destination Market" placeholder="EU · USA · Japan · UAE …" value={form.destination_market} onChange={(v) => setField("destination_market", v)} testid="input-destination" />
                <FormField label="Documentation Needed" placeholder="COA · TDS · regulatory dossier" value={form.documentation_needed} onChange={(v) => setField("documentation_needed", v)} testid="input-docs" />
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="step-request" className="space-y-5">
              <h2 className="font-display text-2xl md:text-3xl text-brand-black">Request Type</h2>
              <div className="space-y-3 mt-2">
                {REQUEST_TYPES.map((r) => {
                  const selected = form.request_type === r.id;
                  return (
                    <button
                      key={r.id}
                      type="button"
                      data-testid={`request-type-${r.id}`}
                      onClick={() => setField("request_type", r.id)}
                      className={`w-full text-left p-5 border transition flex items-start gap-4 min-h-[80px] ${selected ? "border-brand-green bg-brand-green/5" : "border-brand-green/20 hover:border-brand-gold"}`}
                    >
                      <span className={`mt-1 w-4 h-4 rounded-full border-2 shrink-0 ${selected ? "border-brand-green bg-brand-green" : "border-brand-black/40"}`} />
                      <span>
                        <span className="block font-display text-lg sm:text-xl text-brand-black">{r.label}</span>
                        <span className="block text-sm text-brand-black/60 mt-1 leading-relaxed">{r.desc}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="pt-3">
                <Label>Additional notes (optional)</Label>
                <textarea
                  data-testid="input-message"
                  rows={4}
                  value={form.message}
                  onChange={(e) => setField("message", e.target.value)}
                  placeholder="Tell us about your application, regulatory plan, or partnership context…"
                  className="w-full mt-2 px-4 py-3 bg-brand-cream/40 border border-brand-green/20 focus:border-brand-teal focus:outline-none transition text-sm font-body resize-none"
                />
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mt-8 md:mt-10 pt-6 md:pt-8 border-t border-brand-green/10 gap-3">
            <button
              type="button"
              onClick={() => setStep(Math.max(0, step - 1))}
              disabled={step === 0}
              data-testid="form-back"
              className="inline-flex items-center gap-2 text-sm uppercase tracking-widest text-brand-black/65 disabled:opacity-30 hover:text-brand-gold transition min-h-[44px]"
            >
              <ArrowLeft size={14} /> Back
            </button>
            {step < STEPS.length - 1 ? (
              <button
                type="button"
                disabled={!canProceed}
                onClick={() => setStep(step + 1)}
                data-testid="form-next"
                className="btn-primary disabled:opacity-50 min-h-[48px]"
              >
                Continue <ArrowRight size={14} />
              </button>
            ) : (
              <button
                type="button"
                disabled={!canProceed || submitting}
                onClick={submit}
                data-testid="form-submit"
                className="btn-primary disabled:opacity-50 min-h-[48px]"
              >
                {submitting ? <><Loader2 size={14} className="animate-spin" /> Submitting</> : <>Submit Inquiry <ArrowRight size={14} /></>}
              </button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

const Label = ({ children }) => (
  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-black/55">{children}</span>
);

const FormField = ({ label, value, onChange, type = "text", placeholder = "", testid }) => (
  <div>
    <Label>{label}</Label>
    <input
      type={type}
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full mt-2 px-4 py-3 bg-brand-cream/40 border border-brand-green/20 focus:border-brand-teal focus:outline-none transition text-sm font-body min-h-[44px]"
    />
  </div>
);

export default ReserveAllocation;
